// src/pages/TrustDashboardPage.jsx
import React, { useEffect, useState, useMemo, useContext } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Link } from "react-router-dom";
import {
  Shield, ChevronRight, CheckCircle2, Layers, Globe, Edit3, X,ChevronDown
} from "lucide-react";
import useTrustStore from "../../store/useTrustStore";
import BackgroundFooter from "../BackgroundFooter";
import { Col, Row, Form, Spinner, Toast, ToastContainer } from "react-bootstrap";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { Country, City } from "country-state-city";
import { useForm } from "../FormProvider";
import { ScoreContext } from "../ScoreContext";
import Menubar from "../Menubar";
import NavigationBar from "../Nav";
import { fetchFrameworkListApi } from "../../store/trustApi";
import "../../ComponentsCss/TrustDashboardPage.css";
import { logActivity } from '../../utils/activityLogApi';




/* ═══════════════════════════════════════════════
   BRAND COLORS (from ServQual Brand Guidelines)
   Primary   #6BCE8B  (Green  – 70% usage)
   Secondary #035EA5  (Blue   – 20% usage)
   Tertiary  #FFFF00  (Yellow – 10% usage)
   Font: Inter
═══════════════════════════════════════════════ */
const BRAND = {
  primary:      "#6BCE8B",
  primaryDark:  "#4EAD6E",
  primaryLight: "#E8F8EE",
  secondary:    "#035EA5",
  secondaryDark:"#024A82",
  secondaryLight:"#E6F0F9",
  tertiary:     "#FFFF00",
  tertiaryMuted:"#FFF9C4",
  white:        "#FFFFFF",
  dark:         "#1E293B",
  gray50:       "#F8FAFC",
  gray100:      "#F1F5F9",
  gray200:      "#E2E8F0",
  gray400:      "#94A3B8",
  gray600:      "#475569",
  gray900:      "#0F172A",
};


/* ─── Dropdown Options (same as Overview) ─── */
const industryOptions = [
  { label: "Manufacturing", value: "Manufacturing" },
  { label: "Healthcare", value: "Healthcare" },
  { label: "Technology", value: "Technology" },
  { label: "BFSI", value: "BFSI" },
  { label: "Logistics", value: "Logistics" },
  { label: "Retail & E-commerce", value: "Retail & E-commerce" },
  { label: "Education", value: "Education" },
  { label: "Government", value: "Government" },
  { label: "Energy & Utilities", value: "Energy & Utilities" },
  { label: "Telecommunications", value: "Telecommunications" },
  { label: "Media & Entertainment", value: "Media & Entertainment" },
  { label: "Transportation", value: "Transportation" },
  { label: "Pharmaceuticals", value: "Pharmaceuticals" },
  { label: "Hospitality", value: "Hospitality" },
  { label: "Non-Profit", value: "Non-Profit" },
  { label: "Other", value: "Other" },
];

const infrastructureOptions = [
  { label: "Cloud", value: "Cloud" },
  { label: "On-premises", value: "On-premises" },
  { label: "Hybrid", value: "Hybrid" },
  { label: "Multi-cloud", value: "Multi-cloud" },
  { label: "Edge Computing", value: "Edge Computing" },
  { label: "Colocation", value: "Colocation" },
  { label: "Data Center", value: "Data Center" },
  { label: "Other", value: "Other" },
];

const assetOptions = [
  { label: "Network Devices", value: "Network Devices" },
  { label: "Wireless Access Points", value: "Wireless Access Points" },
  { label: "Firewalls", value: "Firewalls" },
  { label: "Servers", value: "Servers" },
  { label: "Workstations & Endpoints", value: "Workstations & Endpoints" },
  { label: "Virtual/Cloud Services", value: "Virtual/Cloud Services" },
  { label: "IoT Devices", value: "IoT Devices" },
  { label: "Mobile Devices", value: "Mobile Devices" },
  { label: "Security Appliances", value: "Security Appliances" },
  { label: "Applications", value: "Applications" },
  { label: "Storage Systems", value: "Storage Systems" },
  { label: "Databases", value: "Databases" },
  { label: "Remote Access Devices", value: "Remote Access Devices" },
  { label: "Other", value: "Other" },
];

const incidentOptions = [
  { label: "Ransomware", value: "Ransomware" },
  { label: "Data Breach", value: "Data Breach" },
  { label: "Phishing Attack", value: "Phishing Attack" },
  { label: "Malware Infection", value: "Malware Infection" },
  { label: "Insider Threat", value: "Insider Threat" },
  { label: "Denial of Service (DoS/DDoS)", value: "Denial of Service (DoS/DDoS)" },
  { label: "Supply Chain Attack", value: "Supply Chain Attack" },
  { label: "Business Email Compromise", value: "Business Email Compromise" },
  { label: "Account Takeover", value: "Account Takeover" },
  { label: "Physical Security Incident", value: "Physical Security Incident" },
  { label: "Zero Day Exploit", value: "Zero Day Exploit" },
  { label: "N/A", value: "N/A" },
  { label: "Other", value: "Other" },
];

const complianceOptions = [
  { label: "ISO 27001", value: "ISO 27001" },
  { label: "SOC 2", value: "SOC 2" },
  { label: "NIST", value: "NIST" },
  { label: "GDPR", value: "GDPR" },
  { label: "HIPAA", value: "HIPAA" },
  { label: "Cyber Essentials", value: "Cyber Essentials" },
  { label: "PCI DSS", value: "PCI DSS" },
  { label: "DPDP Act", value: "DPDP Act" },
  { label: "NIS2", value: "NIS2" },
  { label: "CCPA", value: "CCPA" },
  { label: "FedRAMP", value: "FedRAMP" },
  { label: "ITAR", value: "ITAR" },
  { label: "FISMA", value: "FISMA" },
  { label: "SOX", value: "SOX" },
  { label: "GLBA", value: "GLBA" },
  { label: "CMMC", value: "CMMC" },
  { label: "APRA CPS 234", value: "APRA CPS 234" },
  { label: "PDPA", value: "PDPA" },
  { label: "Other", value: "Other" },
];

const aiUsageOptions = [
  { label: "End-user Chatbot", value: "End-user Chatbot" },
  { label: "Code Assistant", value: "Code Assistant" },
  { label: "Document Automation", value: "Document Automation" },
  { label: "Predictive Analytics", value: "Predictive Analytics" },
  { label: "Anomaly Detection", value: "Anomaly Detection" },
  { label: "Fraud Detection", value: "Fraud Detection" },
  { label: "Image Recognition", value: "Image Recognition" },
  { label: "Voice Assistants", value: "Voice Assistants" },
  { label: "Process Automation (RPA)", value: "Process Automation (RPA)" },
  { label: "Autonomous Vehicles/Robotics", value: "Autonomous Vehicles/Robotics" },
  { label: "Personalized Recommendations", value: "Personalized Recommendations" },
  { label: "Data Extraction", value: "Data Extraction" },
  { label: "Other", value: "Other" },
];

const vendorOptions = [
  { label: "Cisco Systems", value: "Cisco Systems" },
  { label: "Fortinet", value: "Fortinet" },
  { label: "Palo Alto Networks", value: "Palo Alto Networks" },
  { label: "SonicWall", value: "SonicWall" },
  { label: "Check Point", value: "Check Point" },
  { label: "Juniper Networks", value: "Juniper Networks" },
  { label: "Aruba (HPE)", value: "Aruba (HPE)" },
  { label: "Dell Technologies", value: "Dell Technologies" },
  { label: "HP Inc.", value: "HP Inc." },
  { label: "HPE (Hewlett Packard Enterprise)", value: "HPE (Hewlett Packard Enterprise)" },
  { label: "Lenovo", value: "Lenovo" },
  { label: "ASUS", value: "ASUS" },
  { label: "Acer", value: "Acer" },
  { label: "Apple", value: "Apple" },
  { label: "Microsoft", value: "Microsoft" },
  { label: "Google", value: "Google" },
  { label: "Amazon Web Services (AWS)", value: "Amazon Web Services (AWS)" },
  { label: "Oracle", value: "Oracle" },
  { label: "SAP", value: "SAP" },
  { label: "VMware", value: "VMware" },
  { label: "IBM", value: "IBM" },
  { label: "Intel", value: "Intel" },
  { label: "AMD", value: "AMD" },
  { label: "NVIDIA", value: "NVIDIA" },
  { label: "Broadcom", value: "Broadcom" },
  { label: "Qualcomm", value: "Qualcomm" },
  { label: "Micron", value: "Micron" },
  { label: "Western Digital", value: "Western Digital" },
  { label: "Seagate", value: "Seagate" },
  { label: "Trend Micro", value: "Trend Micro" },
  { label: "McAfee", value: "McAfee" },
  { label: "CrowdStrike", value: "CrowdStrike" },
  { label: "SentinelOne", value: "SentinelOne" },
  { label: "Sophos", value: "Sophos" },
  { label: "Bitdefender", value: "Bitdefender" },
  { label: "ESET", value: "ESET" },
  { label: "Kaspersky", value: "Kaspersky" },
  { label: "FireEye / Mandiant", value: "FireEye / Mandiant" },
  { label: "Zscaler", value: "Zscaler" },
  { label: "Okta", value: "Okta" },
  { label: "Ping Identity", value: "Ping Identity" },
  { label: "Duo Security (Cisco)", value: "Duo Security (Cisco)" },
  { label: "OneLogin", value: "OneLogin" },
  { label: "Darktrace", value: "Darktrace" },
  { label: "Rapid7", value: "Rapid7" },
  { label: "Tenable", value: "Tenable" },
  { label: "Qualys", value: "Qualys" },
  { label: "Splunk", value: "Splunk" },
  { label: "LogRhythm", value: "LogRhythm" },
  { label: "AlienVault / AT&T Cybersecurity", value: "AlienVault / AT&T Cybersecurity" },
  { label: "ManageEngine", value: "ManageEngine" },
  { label: "Box", value: "Box" },
  { label: "Dropbox", value: "Dropbox" },
  { label: "Zoom", value: "Zoom" },
  { label: "Slack (Salesforce)", value: "Slack (Salesforce)" },
  { label: "Salesforce", value: "Salesforce" },
  { label: "Atlassian (Jira, Confluence)", value: "Atlassian (Jira, Confluence)" },
  { label: "Notion", value: "Notion" },
  { label: "Asana", value: "Asana" },
  { label: "ServiceNow", value: "ServiceNow" },
  { label: "Freshworks", value: "Freshworks" },
  { label: "Ivanti", value: "Ivanti" },
  { label: "Ivanti Wavelink", value: "Ivanti Wavelink" },
  { label: "Jamf", value: "Jamf" },
  { label: "SailPoint", value: "SailPoint" },
  { label: "CyberArk", value: "CyberArk" },
  { label: "BeyondTrust", value: "BeyondTrust" },
  { label: "Varonis", value: "Varonis" },
  { label: "Symantec (Broadcom)", value: "Symantec (Broadcom)" },
  { label: "Proofpoint", value: "Proofpoint" },
  { label: "Barracuda Networks", value: "Barracuda Networks" },
  { label: "F5 Networks", value: "F5 Networks" },
  { label: "Imperva", value: "Imperva" },
  { label: "Appgate", value: "Appgate" },
  { label: "Forcepoint", value: "Forcepoint" },
  { label: "BigID", value: "BigID" },
  { label: "Virtru", value: "Virtru" },
  { label: "Egnyte", value: "Egnyte" },
  { label: "Rubrik", value: "Rubrik" },
  { label: "Veeam", value: "Veeam" },
  { label: "Cohesity", value: "Cohesity" },
  { label: "Commvault", value: "Commvault" },
  { label: "Acronis", value: "Acronis" },
  { label: "Parallels", value: "Parallels" },
  { label: "Citrix", value: "Citrix" },
  { label: "NetApp", value: "NetApp" },
  { label: "Hitachi Vantara", value: "Hitachi Vantara" },
  { label: "Pure Storage", value: "Pure Storage" },
  { label: "Infinidat", value: "Infinidat" },
  { label: "Wasabi", value: "Wasabi" },
  { label: "DigitalOcean", value: "DigitalOcean" },
  { label: "Linode (Akamai)", value: "Linode (Akamai)" },
  { label: "Akamai", value: "Akamai" },
  { label: "Cloudflare", value: "Cloudflare" },
  { label: "OVHcloud", value: "OVHcloud" },
  { label: "Alibaba Cloud", value: "Alibaba Cloud" },
  { label: "Tencent Cloud", value: "Tencent Cloud" },
  { label: "Huawei Cloud", value: "Huawei Cloud" },
  { label: "Elastic (ELK Stack)", value: "Elastic (ELK Stack)" },
  { label: "Other", value: "Other" },
];


/* ═══════════════════════════════════════════════
   BRAND-ALIGNED react-select STYLES
═══════════════════════════════════════════════ */
const brandSelectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 10001 }),
  control: (base, state) => ({
    ...base,
    fontFamily: "'Inter', sans-serif",
    borderColor: state.isFocused ? BRAND.secondary : BRAND.gray200,
    boxShadow: state.isFocused ? `0 0 0 2px ${BRAND.primaryLight}` : "none",
    "&:hover": { borderColor: BRAND.secondary },
    borderRadius: "10px",
    minHeight: "42px",
    fontSize: "13px",
    backgroundColor: BRAND.white,
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: BRAND.secondaryLight,
    borderRadius: "6px",
    border: `1px solid ${BRAND.secondaryDark}40`,
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: BRAND.secondaryDark,
    fontWeight: 500,
    fontSize: "12px",
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: BRAND.secondaryDark,
    "&:hover": { backgroundColor: `${BRAND.primary}30`, color: BRAND.secondaryDark },
  }),
  option: (base, state) => ({
    ...base,
    fontFamily: "'Inter', sans-serif",
    backgroundColor: state.isSelected
      ? BRAND.secondary
      : state.isFocused
      ? BRAND.primaryLight
      : "transparent",
    color: state.isSelected ? BRAND.white : BRAND.gray900,
    fontSize: "13px",
  }),
  placeholder: (base) => ({
    ...base,
    color: BRAND.gray400,
    fontSize: "13px",
  }),
};


export default function TrustDashboardPage({ userData }) {
    const [frameworkReadinessOpen, setFrameworkReadinessOpen] = useState(false);
  
  const {
    domains, loading, fetchDomains,
    setProfile, primaryOnly, setPrimaryOnly,
    fetchOverview, saveOverview, overviewData, 
    readinessData, readinessLoading, fetchReadiness,
    applicableFrameworks,
  } = useTrustStore();
  const { profile,user} = useAuth();
const orgId = profile?.orgId;
  const { formData, setFormData, updateFormField } = useForm();
  const { setOverviewData } = useContext(ScoreContext);
  const resolvedUserData = userData || {
  cognitousername: user?.["cognito:username"],
  email: user?.email,
  orgId: profile?.orgId,
  firstName: profile?.firstName,
  lastName: profile?.lastName,
};
const userId = resolvedUserData?.cognitousername;
  // const userId = userData?.["cognito:username"];

  // Popup state
  const [showOverviewPopup, setShowOverviewPopup] = useState(false);
  const [overviewCompleted, setOverviewCompleted] = useState(false);
  const [popupSaving, setPopupSaving] = useState(false);

  // City / Country
  const [cityOptions, setCityOptions] = useState([]);
  const [countryError, setCountryError] = useState("");
  const countries = useMemo(() => Country.getAllCountries(), []);

  // Toast
  const [showToast, setShowToast] = useState(false);
  const [toastMsg, setToastMsg] = useState("");
  const [toastVariant, setToastVariant] = useState("success");

  // Sidebar
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() =>
    JSON.parse(localStorage.getItem("isSidebarCollapsed")) || false
  );
  const handleSidebarToggle = () => setIsSidebarCollapsed((prev) => !prev);
const [frameworkOptions, setFrameworkOptions] = useState([]);

useEffect(() => {
  if (!profile) return;
  const loadFrameworks = async () => {
    try {
      const res = await fetchFrameworkListApi({ orgId: profile.orgId });
      const frameworks = res.data?.frameworks || [];
      const options = frameworks.map((fw) => ({ label: fw, value: fw }));
      setFrameworkOptions(options);
    } catch (err) {
      console.error("Failed to load frameworks:", err);
    }
  };
  loadFrameworks();
}, [profile]);

  useEffect(() => {
    if (profile) {
      setProfile(profile);
      if (!domains.length) fetchDomains();
    }
  }, [profile]);
  

  // ★ ADD THIS — Auto-fetch readiness when page loads or frameworks change
   useEffect(() => {
    if (profile && applicableFrameworks?.length > 0) {
      fetchReadiness();
    }
  }, [profile, applicableFrameworks]);





const [trustStep, setTrustStep] = useState(null)
const [checkingOverview, setCheckingOverview] = useState(true)
const [clauseSearch, setClauseSearch] = useState("");
const [filterClause, setFilterClause] = useState('');
const [filterGroup, setFilterGroup] = useState("");







// 2. Single useEffect that decides the step AFTER the async check
useEffect(() => {
  if (!profile) return
  let cancelled = false

  const check = async () => {
    setCheckingOverview(true)
    const data = await fetchOverview()
    if (cancelled) return

    const hasData = data && data.overviewQuestions &&
      data.overviewQuestions.length > 0 &&
      data.overviewQuestions.some(q => q.answer && q.answer.trim() !== '')

    setOverviewCompleted(!!hasData)
    setTrustStep(hasData ? 'GLOBAL' : 'OVERVIEW')  // <-- KEY: decide here
    setCheckingOverview(false)

    if (!hasData) {
      setShowOverviewPopup(true)
    }
  }

  check()
  return () => { cancelled = true }
}, [profile])
  

  // ★ ADD THIS — Repopulate form when overviewData loads ★
  useEffect(() => {
    if (!overviewData || !overviewData.overviewQuestions) return;

    const qs = overviewData.overviewQuestions;
    const getAnswer = (index) => qs[index]?.answer || "";

    const toOptions = (str) =>
      str ? str.split(", ").filter(Boolean).map((v) => ({ label: v, value: v })) : [];

    const toCountryOptions = (str) => {
      if (!str) return [];
      const allCountries = Country.getAllCountries();
      return str.split(", ").filter(Boolean).map((name) => {
        const found = allCountries.find((c) => c.name === name);
        return { label: name, value: found ? found.isoCode : name };
      });
    };

    setFormData((prev) => ({
      ...prev,
      companyName: getAnswer(0),
      businessTypeMulti: toOptions(getAnswer(1)),
      locations: getAnswer(2),
      countries: toCountryOptions(getAnswer(3)),
      cities: toOptions(getAnswer(4)),
      users: getAnswer(5),
      infrastructuretypeMulti: toOptions(getAnswer(6)),
      assetsMulti: toOptions(getAnswer(7)),
      complianceMulti: toOptions(getAnswer(8)),
      securityEventMulti: toOptions(getAnswer(9)),
      generativeAIProductMulti: toOptions(getAnswer(10)),
      vendor: toOptions(getAnswer(11)),
    }));
  }, [overviewData]);


  // City options from selected countries
  useEffect(() => {
    if (formData.countries?.length > 0) {
      let allCities = [];
      formData.countries.forEach((country) => {
        const cities = City.getCitiesOfCountry(country.value) || [];
        allCities = allCities.concat(
          cities.slice(0, 50).map((c) => ({ value: c.name, label: c.name }))
        );
      });
      setCityOptions(allCities);
    } else {
      setCityOptions([]);
    }
  }, [formData.countries]);

  const loadCityOptions = (inputValue) =>
    new Promise((resolve) => {
      if (!inputValue || !inputValue.trim()) {
        let all = [];
        (formData.countries || []).forEach((c) => {
          const cities = City.getCitiesOfCountry(c.value) || [];
          all = all.concat(cities.slice(0, 20).map((ci) => ({ label: ci.name, value: ci.name })));
        });
        resolve(all.slice(0, 50));
        return;
      }
      let results = [];
      (formData.countries || []).forEach((c) => {
        const cities = City.getCitiesOfCountry(c.value) || [];
        results = results.concat(
          cities
            .filter((ci) => ci.name.toLowerCase().includes(inputValue.toLowerCase()))
            .slice(0, 20)
            .map((ci) => ({ label: ci.name, value: ci.name }))
        );
      });
      resolve(results);
    });

  const handleCountryChange = (selectedOptions) => {
    const count = selectedOptions.length;
    const locations = parseInt(formData.locations, 10);
    if (count > locations) {
      setCountryError(`Countries (${count}) cannot exceed locations (${locations}).`);
      return;
    }
    setCountryError("");
    setFormData((prev) => ({ ...prev, countries: selectedOptions }));
  };

  const getUserRegion = () => sessionStorage.getItem("userRegion") || "AP";

  const handlePopupSave = async () => {
  setPopupSaving(true);
  try {
    const success = await saveOverview(formData); // from useTrustStore
    if (success) {
      setOverviewCompleted(true);
      setShowOverviewPopup(false);
      setToastMsg("✓ Overview saved successfully!");
      logActivity({
                        userData,
                        orgId:       orgId,
                        region:      sessionStorage.getItem('userRegion') || 'AP',
                        module:      'Global Compliance & Trust',
                        category:    'overview',
                        action:      'Overview saved',
                        severity:    'success',
                        description: `Overview saved`,});
      setToastVariant("success");
    } else {
      throw new Error("Save failed");
    }
  } catch (err) {
    setToastMsg("❌ Failed to save overview data.");
    setToastVariant("danger");
  } finally {
    setPopupSaving(false);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  }
};
  // Dashboard computed
  const totalDomains = domains.length;
  const totalPrimaryFrameworks = domains.reduce((s, d) => s + (d.primaryCount || 0), 0);
  const totalSecondaryFrameworks = domains.reduce((s, d) => s + (d.secondaryCount || 0), 0);
  const totalFrameworks = domains.reduce((s, d) => s + (d.totalFrameworks || 0), 0);
  const domainGroups = [...new Set(domains.map((d) => d.domainGroup).filter(Boolean))];
  


  // Sort domains to match SUSAN Engine control hierarchy (GOV → RISK → ASSET → ...)
  const DOMAIN_SORT_ORDER = {
    GOV: 1, RISK: 2, ASSET: 3, IAM: 4, DATA: 5, NET: 6,
    APP: 7, OPS: 8, INC: 9, BCP: 10, COMP: 11, HR: 12,
    PHY: 13, CLOUD: 14, VENDOR: 15, PRIV: 16,TIC: 17,
  };

const sortedDomains = [...domains].sort((a, b) => {
    const prefixA = (a.domainId || "").split("-")[0].toUpperCase();
    const prefixB = (b.domainId || "").split("-")[0].toUpperCase();
    return (DOMAIN_SORT_ORDER[prefixA] ?? 99) - (DOMAIN_SORT_ORDER[prefixB] ?? 99);
  });

const allClauses = useMemo(() => {
  const clauseSet = new Set();
  domains.forEach(domain => {
    domain.clauses?.forEach(c => {
      const clean = c.replace(/^MANUAL_SEEDED\s*/i, '').trim();
      if (clean) clauseSet.add(clean);
    });
  });

  return [...clauseSet].sort();
}, [domains,  domains.length]);




const filteredDomains = useMemo(() => {
  return sortedDomains.filter(domain => {
    const q = clauseSearch.toLowerCase();
    const matchesSearch = !q ||
      domain.domainId?.toLowerCase().includes(q) ||
      domain.domainName?.toLowerCase().includes(q) ||
      domain.domainGroup?.toLowerCase().includes(q) ||
      domain.clauses?.some(c => c.toLowerCase().includes(q));

    const matchesGroup = !filterGroup || domain.domainGroup === filterGroup;

    // Clean clause matching
const matchesClause = !filterClause ||
  domain.clauses?.some(c => {
    const cleaned = c.replace(/^MANUAL_SEEDED\s*/i, '').trim().toLowerCase();
    const filter = filterClause.toLowerCase();
   
    return cleaned.includes(filter);
  });

    return matchesSearch && matchesGroup && matchesClause;
  });
}, [sortedDomains, clauseSearch, filterGroup, filterClause]); // ADD filterClause to deps

  

    if (loading && !domains.length) {
    return (
      <div style={{
        minHeight: "100vh",
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        background: BRAND.gray50, fontFamily: "'Inter', sans-serif",
        gap: "16px",
      }}>
        <div style={{
          width: "48px", height: "48px", borderRadius: "50%",
          border: `3px solid ${BRAND.gray200}`,
          borderTopColor: BRAND.primary,
          animation: "spin 0.8s linear infinite",
        }} />
        <div style={{ textAlign: "center" }}>
          <p style={{ fontSize: "14px", fontWeight: 600, color: BRAND.gray900, margin: "0 0 4px" }}>
            Loading Trust Controls
          </p>
          <p style={{ fontSize: "12px", color: BRAND.gray400, margin: 0 }}>
            Fetching compliance data...
          </p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }



  
  const renderOverviewPopup = () => {
    if (!showOverviewPopup) return null;
  };

    const inputStyle = {
      fontFamily: "'Inter', sans-serif",
      borderRadius: "10px",
      fontSize: "13px",
      padding: "10px 14px",
      border: `1px solid ${BRAND.gray200}`,
      transition: "border-color 0.2s, box-shadow 0.2s",
      outline: "none",
    };
    const labelStyle = {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 600,
      fontSize: "13.5px",
      color: BRAND.dark,
      marginBottom: "6px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    };
    const qNumStyle = {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: "24px",
      height: "24px",
      borderRadius: "50%",
      fontSize: "11px",
      fontWeight: 700,
      color: BRAND.dark,
      flexShrink: 0,
    };

    
if (checkingOverview || trustStep === null) {
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      background: BRAND.gray50, fontFamily: 'Inter, sans-serif', gap: 16
    }}>
      <Spinner animation="border" style={{ color: BRAND.primary }} />
      <p style={{ fontSize: 14, fontWeight: 600, color: BRAND.gray900 }}>
  Loading Trust Controls...
</p>
<p style={{ fontSize: 12, color: BRAND.gray400, margin: 0 }}>
  Fetching compliance data...
</p>
    </div>
  )
}

  /* ═══════════════════════════════════════════════
     MAIN RENDER
  ═══════════════════════════════════════════════ */
  return (
    <Row className="g-0">
      <Col
        xs={12}
        className={`bg-transparent sidebar-container ${isSidebarCollapsed ? "collapsed" : "hello"}`}
        style={{ width: isSidebarCollapsed ? "90px" : "317px", transition: "width 0.3s ease" }}
      >
        <Menubar isCollapsed={isSidebarCollapsed} onToggle={handleSidebarToggle} />
      </Col>

      <Col
        className="pt-0 px-0 ps-0 section-container"
        style={{ width: `calc(100% - ${isSidebarCollapsed ? "90px" : "317px"})`, transition: "width 0.3s ease" }}
      >
        <NavigationBar userData={resolvedUserData} isSidebarCollapsed={isSidebarCollapsed} />
        <Col className="overflow-auto p-0" style={{ height: "calc(100vh - 60px)" }}>


  <div
  style={{
    padding: "16px 28px",
    background: "linear-gradient(180deg,#f9fafb,#eef2ff)",
    marginBottom: 22,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    border: `1px solid ${BRAND.gray200}`,
  }}
>
  {/* Left: 3 steps */}
  <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
    {[
      { id: "OVERVIEW", label: "Overview", subtitle: "Learn the basics", index: 1 },
      { id: "CONTEXT", label: "Context", subtitle: "Customize your scope", index: 2 },
      { id: "GLOBAL", label: "Global Trust", subtitle: "Compliance control", index: 3 },
    ].map((step, idx, arr) => {
      const isActive = trustStep === step.id;
      const isDone =
        (step.id === "CONTEXT" && trustStep === "GLOBAL") ||
        (step.id === "GLOBAL" && false);

      return (
        <React.Fragment key={step.id}>
          <button
            onClick={() => setTrustStep(step.id)}
            style={{
              border: "none",
              background: "transparent",
              display: "flex",
              alignItems: "center",
              gap: 10,
              cursor: "pointer",
              padding: "4px 10px",
            }}
          >
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: "999px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 13,
                fontWeight: 700,
                color: isActive || isDone ? BRAND.white : BRAND.gray600,
                background: isDone
                  ? BRAND.primary
                  : isActive
                  ? BRAND.secondary
                  : BRAND.gray200,
                boxShadow:
                  isActive || isDone
                    ? "0 0 0 2px rgba(255,255,255,0.9)"
                    : "none",
              }}
            >
              {isDone ? <CheckCircle2 size={16} /> : step.index}
            </div>
            <div style={{ textAlign: "left" }}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: isActive ? BRAND.dark : BRAND.gray600,
                }}
              >
                {step.label}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: BRAND.gray400,
                }}
              >
                {step.subtitle}
              </div>
            </div>
          </button>

          {/* connector line between steps */}
          {idx < arr.length - 1 && (
            <div
              style={{
                width: 40,
                height: 2,
                borderRadius: 999,
                background:
                  trustStep === "GLOBAL" && step.id !== "GLOBAL"
                    ? BRAND.primary
                    : trustStep === "CONTEXT" && step.id === "OVERVIEW"
                    ? BRAND.secondary
                    : BRAND.gray200,
                opacity: isDone || isActive ? 1 : 0.7,
              }}
            />
          )}
        </React.Fragment>
      );
    })}
  </div>

  {/* Right: context chip */}
  {/* <div
    style={{
      padding: "6px 16px",
      borderRadius: "999px",
      background: "rgba(3,94,165,0.08)",
      color: BRAND.secondaryDark,
      fontSize: 11,
      fontWeight: 600,
      display: "flex",
      alignItems: "center",
      gap: 6,
    }}
  >
    <Shield size={14} />
    Global Trust · SUSAN Master Controls
  </div> */}
</div>



{/* Main content switch */}
{trustStep === "OVERVIEW" && (
  <div style={{ padding: "24px 40px" }}>
    {/* Big hero card */}
    <div
  style={{
     borderRadius: "24px",
    background: BRAND.secondaryDark,
    color: BRAND.white,
    padding: "40px 44px",
    position: "relative",
    overflow: "hidden",
  }}
>
      {/* subtle circle on right like design */}
      <div
        style={{
          position: "absolute",
          right: "-80px",
          top: "-40px",
          width: "320px",
          height: "320px",
          borderRadius: "999px",
          opacity: 0.6,
        }}
      />

      <div style={{ position: "relative", zIndex: 1, maxWidth: 520 }}>
        <div
          style={{
           display: "inline-flex",
            padding: "6px 14px",
            borderRadius: "999px",
            fontSize: 11,
            letterSpacing: 1.4,
            textTransform: "uppercase", 
            fontWeight: 600,
            backgroundColor:BRAND.white ,    
            border: "1px solid rgba(148,163,184,0.4)",
            color:BRAND.secondary
          }}
        >
         <div
    style={{
      padding: "6px 16px",
      borderRadius: "999px",
      fontSize: 13,
      fontWeight: 750,
      display: "flex",
      alignItems: "center",
      gap: 6,
    }}
  >
    Global Compliance & Trust
  </div> 
        </div>

        <h2
          style={{
            margin: "18px 0 8px",
            fontSize: 30,
            lineHeight: 1.2,
            fontWeight: 700,
          }}
        >
          Compliance Control Made Intelligent
        </h2>

        <p
          style={{
            margin: 0,
            fontSize: 14,
            lineHeight: 1.6,
            opacity: 0.9,
          }}
        >
          Global trust provides you a unified view that brings clarity to controls and specifies how trust is understood.
        </p>

        <button
          onClick={() => setTrustStep("CONTEXT")}
          style={{
            marginTop: 26,
            padding: "11px 30px",
            borderRadius: "999px",
            border: "none",
            background: "linear-gradient(135deg,#6BCE8B,#4EAD6E)",
            color: "#FFFF",
            fontWeight: 700,
            fontSize: 13,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          Next Step
          <ChevronRight size={16} />
        </button>
      </div>
    </div>

    {/* Feature cards row */}
   <div
  style={{
    display: "grid",
    gridTemplateColumns: "repeat(3,minmax(0,1fr))",
    gap: 20,
    marginTop: 24,
  }}
>
  {[
    {
      title: "Smart Filtering",
      desc: "Quickly narrow down controls using filters across domains, frameworks, and categories. This helps you focus on the most relevant controls, identify gaps faster, and streamline your review process without navigating through unnecessary data.",
      accent: BRAND.primary,
    },
    {
      title: "Multi-Framework Visibility",
      desc: "Gain a unified view of controls aligned across multiple regulatory and industry frameworks. Understand how a single control applies across standards, reduce duplication, and get complete visibility into your compliance landscape in one place.",
      accent: BRAND.secondary,
    },
    {
      title: "Policies & Evidence Access",
      desc: "Easily add linked policies and supporting evidence for each control. This ensures better transparency, simplifies audits, and helps you quickly verify compliance status without searching across multiple systems",
      accent: "#F59E0B",
    },
  ].map((card) => (
    <div  className=" hover:shadow-md transition-all group"
      key={card.title}
      style={{
        position: "relative",
        borderRadius: "20px",
        padding: "18px 20px 20px",
        border: `1px solid ${BRAND.gray200}`,
        overflow: "hidden",
        cursor: "default",
        transition: "all 0.3s ease",
        background:"#F5F7FA",
       
      }}
      onMouseEnter={(e) => e.currentTarget.style.borderColor = BRAND.gray200}
      onMouseLeave={(e) => e.currentTarget.style.borderColor = BRAND.gray200}
    >
      {/* subtle accent bar on top-right */}
      <div
        style={{
          position: "absolute",
          right: -40,
          top: -40,
          width: 120,
          height: 120,
          borderRadius: "999px",
        }}
      />

      {/* icon + title */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: 10,
          position: "relative",
          zIndex: 1,
          marginLeft:"-29px"
        }}
      >
        <div
          style={{
            width: 30,
            height: 30,
            borderRadius: "999px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 16,
          }}
        >
          {card.emoji}
        </div>
        <div
          style={{
             fontSize: 16,
            fontWeight: 700,
            color: BRAND.secondary
          }}
        >
          {card.title}
        </div>
      </div>

      {/* description */}
      <p
        style={{
          margin: 0,
          fontSize: 13,
          color: BRAND.gray500,
          lineHeight: 1.55,
          position: "relative",
          zIndex: 1,
        }}
      >
        {card.desc}
      </p>
    </div>
  ))}
</div>

  </div>
)}



{trustStep === "CONTEXT" && (
        
  <div
    style={{
      marginTop: 16,
      borderRadius: "20px",
      width: "100%",
      maxWidth: "980px",
      marginInline: "auto",
      maxHeight: "72vh",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      background: BRAND.white,
      fontFamily: "'Inter', sans-serif",
      border:"1px solid #CBD5E1"
    }}
  >
    
    {/* Header */}
    <div
      style={{
        padding: "22px 28px",
        // background: `linear-gradient(135deg, ${BRAND.secondary} 0%, ${BRAND.primaryDark} 100%)`,
         background: BRAND.secondaryDark,
        color: BRAND.white,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <div>
        <h4
          style={{
            margin: 0,
            fontWeight: 700,
            fontSize: "18px",
            fontFamily: "'Inter', sans-serif",
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          Organizational Scope
        </h4>
        <p
          style={{
            margin: "4px 0 0",
            opacity: 0.9,
            fontSize: "13px",
            fontFamily: "'Inter', sans-serif",
          }}
        >
          Complete your organization profile to personalize your Trust Controls
          experience
        </p>
      </div>

      {/* Close X → back to Overview */}
      <button
        onClick={() => setTrustStep("OVERVIEW")}
        style={{
          background: "rgba(255,255,255,0.2)",
          border: "none",
          color: BRAND.white,
          borderRadius: "10px",
          padding: "8px",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "background 0.2s",
        }}
        onMouseEnter={(e) =>
          (e.currentTarget.style.background = "rgba(255,255,255,0.35)")
        }
        onMouseLeave={(e) =>
          (e.currentTarget.style.background = "rgba(255,255,255,0.2)")
        }
      >
        <X size={18} />
      </button>
    </div>

    
    {/* Body – Q1–Q12 same as pehle */}
    <div
      style={{
        flex: 1,
        overflowY: "auto",
        padding: "28px 32px",
        background: BRAND.gray50,
      }}
    >
      <Form>
              {/* Q1 */}
              <Form.Group className="mb-4">
                              <Form.Label style={labelStyle}><span style={qNumStyle}>1</span> What is your company name?</Form.Label>
                              <Form.Control type="text" placeholder="Enter your company name"
                                value={formData.companyName || ""} style={inputStyle} maxLength={100}
                                onChange={(e) => updateFormField("companyName", e.target.value)}
                                onFocus={(e) => { e.target.style.borderColor = BRAND.secondary; e.target.style.boxShadow = `0 0 0 3px ${BRAND.primaryLight}`; }}
                                onBlur={(e) => { e.target.style.borderColor = BRAND.gray200; e.target.style.boxShadow = "none"; }}
                              />
                            </Form.Group>

              {/* Q2 */}
              <Form.Group className="mb-4">
                <Form.Label style={labelStyle}><span style={qNumStyle}>2</span> Select your industry sector</Form.Label>
                <Select isMulti options={industryOptions} value={formData.businessTypeMulti}
                  onChange={(sel) => updateFormField("businessTypeMulti", sel)}
                  classNamePrefix="react-select" menuPortalTarget={document.body} styles={brandSelectStyles}
                />
                {formData.businessTypeMulti?.some((o) => o.value === "Other") && (
                  <Form.Control type="text" className="mt-2" placeholder="Specify other industry" style={inputStyle}
                    value={formData.otherIndustry || ""} onChange={(e) => updateFormField("otherIndustry", e.target.value)} />
                )}
              </Form.Group>

              {/* Q3 */}
             <Form.Group className="mb-4">
                             <Form.Label style={labelStyle}><span style={qNumStyle}>3</span> Number of organizational locations</Form.Label>
                             <Form.Control type="number" placeholder="0" min="0" style={inputStyle}
                               value={formData.locations ?? ""} onChange={(e) => setFormData({ ...formData, locations: e.target.value })}
                               onFocus={(e) => { e.target.style.borderColor = BRAND.secondary; e.target.style.boxShadow = `0 0 0 3px ${BRAND.primaryLight}`; }}
                               onBlur={(e) => { e.target.style.borderColor = BRAND.gray200; e.target.style.boxShadow = "none"; }}
                             />
                           </Form.Group>

              {/* Q4 */}
              <Form.Group className="mb-4">
                <Form.Label style={labelStyle}><span style={qNumStyle}>4</span> Countries where you operate</Form.Label>
                <Select isMulti
                  options={countries.map((c) => ({ value: c.isoCode, label: c.name }))}
                  value={formData.countries} onChange={handleCountryChange}
                  isDisabled={!formData.locations || formData.locations <= 0}
                  classNamePrefix="react-select" menuPortalTarget={document.body} styles={brandSelectStyles}
                />
                {countryError && <div style={{ color: "#DC2626", marginTop: 5, fontSize: "12px", fontFamily: "'Inter', sans-serif" }}>{countryError}</div>}
              </Form.Group>

              {/* Q5 */}
              <Form.Group className="mb-4">
                <Form.Label style={labelStyle}><span style={qNumStyle}>5</span> Cities in your operations</Form.Label>
                <AsyncSelect isMulti cacheOptions defaultOptions={cityOptions} loadOptions={loadCityOptions}
                  value={formData.cities} onChange={(sel) => setFormData((prev) => ({ ...prev, cities: sel }))}
                  isDisabled={!formData.locations || formData.locations <= 0 || !formData.countries?.length}
                  classNamePrefix="react-select" menuPortalTarget={document.body} styles={brandSelectStyles}
                  placeholder={formData.countries?.length ? "Type to search cities..." : "Select countries first"}
                />
              </Form.Group>

              {/* Q6 */}
              <Form.Group className="mb-4">
                              <Form.Label style={labelStyle}><span style={qNumStyle}>6</span> Approximate number of users</Form.Label>
                              <Form.Control type="number" placeholder="0" min="0" style={inputStyle}
                                value={formData.users ?? ""} onChange={(e) => setFormData({ ...formData, users: e.target.value })}
                                onFocus={(e) => { e.target.style.borderColor = BRAND.secondary; e.target.style.boxShadow = `0 0 0 3px ${BRAND.primaryLight}`; }}
                                onBlur={(e) => { e.target.style.borderColor = BRAND.gray200; e.target.style.boxShadow = "none"; }}
                              />
                            </Form.Group>

              {/* Q7 */}
              <Form.Group className="mb-4">
                <Form.Label style={labelStyle}><span style={qNumStyle}>7</span> Infrastructure type</Form.Label>
                <Select isMulti options={infrastructureOptions} value={formData.infrastructuretypeMulti}
                  onChange={(sel) => updateFormField("infrastructuretypeMulti", sel)}
                  classNamePrefix="react-select" menuPortalTarget={document.body} styles={brandSelectStyles}
                />
                {formData.infrastructuretypeMulti?.some((o) => o.value === "Other") && (
                  <Form.Control type="text" className="mt-2" placeholder="Specify other" style={inputStyle}
                    value={formData.otherInfra || ""} onChange={(e) => updateFormField("otherInfra", e.target.value)} />
                )}
              </Form.Group>

              {/* Q8 */}
              <Form.Group className="mb-4">
                <Form.Label style={labelStyle}><span style={qNumStyle}>8</span> Core IT assets</Form.Label>
                <Select isMulti options={assetOptions} value={formData.assetsMulti}
                  onChange={(sel) => updateFormField("assetsMulti", sel)}
                  classNamePrefix="react-select" menuPortalTarget={document.body} styles={brandSelectStyles}
                />
                {formData.assetsMulti?.some((o) => o.value === "Other") && (
                  <Form.Control type="text" className="mt-2" placeholder="Specify other" style={inputStyle}
                    value={formData.otherAssets || ""} onChange={(e) => updateFormField("otherAssets", e.target.value)} />
                )}
              </Form.Group>

              {/* Q9 */}
<Form.Group className="mb-4">
  <Form.Label style={labelStyle}>
    <span style={qNumStyle}>9</span> Applicable compliance standards
  </Form.Label>
  <Select
    isMulti
    options={frameworkOptions}
    value={formData.complianceMulti}
    onChange={(sel) => updateFormField("complianceMulti", sel)}
    classNamePrefix="react-select"
    menuPortalTarget={document.body}
    styles={brandSelectStyles}
    placeholder="Search and select frameworks..."
    closeMenuOnSelect={false}
  />
  {formData.complianceMulti?.length > 0 && (
    <div style={{
      marginTop: 8, fontSize: "12px", color: BRAND.gray600,
      fontFamily: "'Inter', sans-serif",
    }}>
      ✓ {formData.complianceMulti.length} framework(s) selected
    </div>
  )}
</Form.Group>


              {/* Q10 */}
              <Form.Group className="mb-4">
                <Form.Label style={labelStyle}><span style={qNumStyle}>10</span> Past security incidents</Form.Label>
                <Select isMulti options={incidentOptions} value={formData.securityEventMulti}
                  onChange={(sel) => updateFormField("securityEventMulti", sel)}
                  classNamePrefix="react-select" menuPortalTarget={document.body} styles={brandSelectStyles}
                />
                {formData.securityEventMulti?.some((o) => o.value === "Other") && (
                  <Form.Control type="text" className="mt-2" placeholder="Specify other" style={inputStyle}
                    value={formData.otherSecurityEvent || ""} onChange={(e) => updateFormField("otherSecurityEvent", e.target.value)} />
                )}
              </Form.Group>

              {/* Q11 */}
              <Form.Group className="mb-4">
                <Form.Label style={labelStyle}><span style={qNumStyle}>11</span> Use of Generative AI</Form.Label>
                <Select isMulti options={aiUsageOptions} value={formData.generativeAIProductMulti}
                  onChange={(sel) => updateFormField("generativeAIProductMulti", sel)}
                  classNamePrefix="react-select" menuPortalTarget={document.body} styles={brandSelectStyles}
                />
                {formData.generativeAIProductMulti?.some((o) => o.value === "Other") && (
                  <Form.Control type="text" className="mt-2" placeholder="Specify other" style={inputStyle}
                    value={formData.otherAI || ""} onChange={(e) => updateFormField("otherAI", e.target.value)} />
                )}
              </Form.Group>

              {/* Q12 */}
              <Form.Group className="mb-2">
                <Form.Label style={labelStyle}><span style={qNumStyle}>12</span> Primary technology / cybersecurity vendors</Form.Label>
                <Select isMulti options={vendorOptions} value={formData.vendor || []}
                  onChange={(sel) => updateFormField("vendor", sel)} isClearable
                  placeholder="Search and select vendor..."
                  classNamePrefix="react-select" menuPortalTarget={document.body} styles={brandSelectStyles}
                />
                {Array.isArray(formData.vendor) && formData.vendor.some((v) => v.value === "Other") && (
                  <Form.Control type="text" className="mt-2" placeholder="Enter vendor name" style={inputStyle}
                    value={formData.otherVendorName || ""} onChange={(e) => updateFormField("otherVendorName", e.target.value)} />
                )}
              </Form.Group>
            </Form>
    </div>

    {/* Footer */}
    <div
      style={{
        padding: "16px 28px",
        borderTop: `1px solid ${BRAND.gray200}`,
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        gap: "12px",
        background: BRAND.white,
        fontFamily: "'Inter', sans-serif",
      }}
    >
      {/* Cancel → back to Overview */}
      <button
        onClick={() => setTrustStep("OVERVIEW")}
        style={{
          padding: "10px 28px",
          borderRadius: "10px",
          fontWeight: 600,
          fontSize: "13px",
          border: `1px solid ${BRAND.gray200}`,
          background: BRAND.secondary,
          color: BRAND.white,
          cursor: "pointer",
          fontFamily: "'Inter', sans-serif",
          transition: "all 0.2s",
        }}
        // onMouseEnter={(e) => {
        //   e.currentTarget.style.borderColor = BRAND.secondary;
        //   e.currentTarget.style.color = BRAND.secondary;
        // }}
        // onMouseLeave={(e) => {
        //   e.currentTarget.style.borderColor = BRAND.gray200;
        //   e.currentTarget.style.color = BRAND.gray600;
        // }}
      >
        Cancel
      </button>

      {/* Save & Continue → save + Global dashboard */}
       <button
              onClick={async () => {
                await handlePopupSave();
                setTrustStep("GLOBAL");
              }}
              disabled={popupSaving}
              style={{
                padding: "10px 36px",
                borderRadius: "10px",
                border: "none",
                fontWeight: 600,
                fontSize: "13px",
                color: BRAND.white,
                fontFamily: "'Inter', sans-serif",
                cursor: popupSaving ? "not-allowed" : "pointer",
                background: popupSaving ? BRAND.gray400 : BRAND.secondary,
                display: "flex",
                alignItems: "center",
                gap: "8px",
                boxShadow: popupSaving ,
                transition: "all 0.2s",
              }}
              // onMouseEnter={(e) => {
              //   if (!popupSaving) e.currentTarget.style.background = BRAND.secondaryDark;
              // }}
              // onMouseLeave={(e) => {
              //   if (!popupSaving) e.currentTarget.style.background = BRAND.secondary;
              // }}
            >
              {popupSaving && <Spinner animation="border" size="sm" />}
              {popupSaving ? "Saving..." : "Save & Continue"}
            </button>
    </div>
  </div>
)}


{trustStep === "GLOBAL" && (
          <div className="bg-gray-50 text-gray-900" style={{ fontFamily: "'Inter', sans-serif" }}>
            <div className="max-w-7xl mx-auto px-6 py-8">

              {/* Header */}
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-2xl font-bold text-gray-900">Trust Controls</h1>
                  <span className="px-3 py-1 text-xs border rounded-full" style={{
                    background: BRAND.primaryLight, color: BRAND.primaryDark, borderColor: `${BRAND.primary}40`
                  }}>Active</span>

                  {overviewCompleted && (
                    <button
                     onClick={() => setTrustStep("CONTEXT")}
                      className="ml-4 flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold border rounded-lg transition-all"
                      style={{ background: BRAND.white, borderColor: BRAND.gray200, color: BRAND.gray600 }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = BRAND.primary; e.currentTarget.style.color = BRAND.primaryDark; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = BRAND.gray200; e.currentTarget.style.color = BRAND.gray600; }}
                      title="Edit Organization Overview"
                    >
                      <Edit3 size={14} />
                      Edit Overview
                    </button>
                  )} 
                </div>
                <p style={{ color: BRAND.gray400 }}>
                  SUSAN Master Controls • 150+ controls across {totalDomains} domains
                </p>
              </div>
              {/* ★ Active Framework Filter Indicator — add RIGHT ABOVE Stats Row */}
              {useTrustStore.getState().applicableFrameworks?.length > 0 && (
                <div style={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "16px",
                  padding: "12px 16px",
                  background: BRAND.secondaryLight,
                  borderRadius: "10px",
                  border: `1px solid ${BRAND.secondary}30`,
                  fontFamily: "'Inter', sans-serif",
                }}>
                  <span style={{
                    fontSize: "15px",
                    fontWeight: 700,
                    color: BRAND.secondary,
                  }}>
                    Filtered by:
                  </span>
                  {useTrustStore.getState().applicableFrameworks.map((fw) => (
                    <span key={fw} style={{
                      padding: "4px 12px",
                      borderRadius: "20px",
                      fontSize: "12px",
                      fontWeight: 600,
                      background: BRAND.white,
                      color: BRAND.secondary,
                      border: `1px solid ${BRAND.secondary}40`,
                    }}>
                      {fw}
                    </span>
                  ))}
                  <span style={{
                    fontSize: "11px",
                    color: BRAND.gray400,
                    marginLeft: "auto",
                  }}>
                    Edit Overview → Q9 to change
                  </span>
                </div>
              )}

             
              {/* ★ Framework Readiness Section */}
              {applicableFrameworks?.length > 0 && (
                 <div style={{
                    marginBottom: "20px",
                    background: BRAND.white,
                    border: `1px solid ${BRAND.gray200}`,
                    borderRadius: "10px",
                    overflow: "hidden",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                  }}>
                    {/* Accordion Header - only this div changed */}
                    <div
                      onClick={() => setFrameworkReadinessOpen(prev => !prev)}
                      style={{
                        padding: "14px 20px",
                        borderBottom: frameworkReadinessOpen ? `1px solid ${BRAND.gray200}` : "none",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        position: "sticky",
                        top: 0,
                        background: BRAND.white,
                        zIndex: 2,
                        cursor: "pointer",
                        userSelect: "none",
                      }}
                    >
                      <span style={{
                        fontSize: "17px", fontWeight: 700,
                        color: BRAND.gray900, fontFamily: "'Inter', sans-serif",
                        flex: 1,
                      }}>
                        Framework Readiness
                      </span>
                      {readinessLoading && (
                        <Spinner animation="border" size="sm" style={{ color: BRAND.gray400, marginRight: "8px" }} />
                      )}
                      <ChevronDown
                        size={18}
                        style={{
                          color: BRAND.gray400,
                          transition: "transform 0.3s ease",
                          transform: frameworkReadinessOpen ? "rotate(180deg)" : "rotate(0deg)",
                        }}
                      />
                    </div>
                
                    {/* Accordion Body - wrapped your grid in a collapsible div */}
                    <div style={{
                      maxHeight: frameworkReadinessOpen ? 420 : 0,
                      overflow: "hidden",
                      transition: "max-height 0.35s ease",
                    }}>
                      <div 
                      className="framework-readiness-scroll" 
                      style={{
                        padding: "16px 20px",
                        display: "grid",
                        gridTemplateColumns: "repeat(3, 1fr)",
                        gap: "14px",
                        maxHeight: 340,
                        overflowY: "auto",
                      }}>
                    {((readinessData?.readiness?.length > 0) ? readinessData.readiness : applicableFrameworks.map((fw) => ({
                      framework: fw, totalDomains: 0, totalMappings: 0,
                      passCount: 0, partialCount: 0, gapCount: 0, readinessPercent: 0,
                    }))).map((fw) => {
                      const r = fw.readinessPercent || 0;
                      const total = (fw.passCount || 0) + (fw.partialCount || 0) + (fw.gapCount || 0);
                      const barColor = r >= 80 ? BRAND.primary : r >= 50 ? "#F59E0B" : r > 0 ? "#EF4444" : BRAND.gray300;
                      const labelColor = r >= 80 ? BRAND.primaryDark : r >= 50 ? "#A16207" : r > 0 ? "#991B1B" : BRAND.gray500;
                      const labelBg = r >= 80 ? BRAND.primaryLight : r >= 50 ? "#FEF9C3" : r > 0 ? "#FEE2E2" : BRAND.gray100;
                      const labelText = r >= 80 ? "Ready" : r >= 50 ? "In Progress" : r > 0 ? "Needs Work" : "Not Started";

                      return (
            <div key={fw.framework} style={{
  padding: "16px",
  background: BRAND.gray50,
  borderRadius: "12px",
  border: `1px solid ${BRAND.gray100}`,
  position: "relative",
}}>
  {/* Status label - top right */}
  <span style={{
    position: "absolute",
    top: "12px",
    right: "12px",
    padding: "3px 10px", borderRadius: "6px",
    fontSize: "11px", fontWeight: 600,
    background: labelBg, color: labelColor,
  }}>
    {labelText}
  </span>

  <div style={{
    fontSize: "12px", fontWeight: 600,
    color: BRAND.secondary, marginBottom: "12px",
    fontFamily: "'Inter', sans-serif",
    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
    paddingRight: "80px",
  }} title={fw.framework}>
    {fw.framework}
  </div>

  {/* Progress bar */}
  <div style={{
    height: "8px", borderRadius: "4px",
    background: BRAND.gray200, marginBottom: "10px",
    overflow: "hidden",
  }}>
    <div style={{
      height: "100%", borderRadius: "4px",
      width: `${r}%`,
      background: barColor,
      transition: "width 0.6s ease",
    }} />
  </div>

  {/* Percentage + stats */}
  <div style={{
    display: "flex", justifyContent: "space-between",
    alignItems: "baseline", marginBottom: "10px",
  }}>
    <span style={{
      fontSize: "24px", fontWeight: 800,
      color: labelColor, lineHeight: 1,
    }}>
      {r}%
    </span>
    <div style={{
      fontSize: "11px", color: BRAND.gray500,
      textAlign: "right", lineHeight: 1.5,
      fontFamily: "'Inter', sans-serif",
    }}>
      <div>{fw.totalDomains || 0} domains</div>
      <div>{fw.totalMappings || 0} controls</div>
    </div>
  </div>

  {/* Status counts */}
  {total > 0 && (
    <div style={{
      display: "flex", gap: "8px", fontSize: "11px",
      fontFamily: "'Inter', sans-serif",
    }}>
      <span style={{ color: "#03543F" }}>● {fw.passCount} Pass</span>
      <span style={{ color: "#92400E" }}>● {fw.partialCount} Partial</span>
      <span style={{ color: "#9B1C1C" }}>● {fw.gapCount} Gap</span>
    </div>
  )}
</div>
          );
        })}
      </div>
    </div>
  </div>
)}



              

              {/* Stats Row */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                {[
                  { label: "Domains", value: totalDomains, icon: Layers, accent: BRAND.primary },
                  { label: "Primary Frameworks", value: totalPrimaryFrameworks, icon: Globe, accent: BRAND.secondary },
                  { label: "Secondary Frameworks", value: totalSecondaryFrameworks, icon: Globe, accent: BRAND.primaryDark },
                  { label: "Total Frameworks", value: totalFrameworks, icon: Shield, accent: BRAND.secondary },
                  { label: "Domain Groups", value: domainGroups.length, icon: CheckCircle2, accent: BRAND.primary },
                ].map((stat) => (
                  <div key={stat.label} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs text-gray-500 uppercase" style={{marginLeft:"2px"}}>{stat.label}</span>
                    </div>
                    <span className="text-2xl font-bold text-gray-900">{stat.value}</span>
                  </div>
                ))}
              </div>

              {/* All Domains — flat list, 4 per row */}
             {/* Search + Filter Bar */}
              <div style={{
                display: "flex", gap: 10, marginBottom: 18,
                flexWrap: "wrap", alignItems: "center",
              }}>
                {/* Search input */}
                <div style={{ position: "relative", flex: "1 1 260px", minWidth: 220 }}>
                  <svg
                    style={{
                      position: "absolute", left: 12, top: "50%",
                      transform: "translateY(-50%)", pointerEvents: "none",
                    }}
                    width={15} height={15} viewBox="0 0 24 24" fill="none"
                    stroke={BRAND.gray400} strokeWidth={2.2} strokeLinecap="round"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search by domain ID, name, or group…"
                    value={clauseSearch}
                    onChange={(e) => setClauseSearch(e.target.value)}
                    style={{
                      width: "100%", paddingLeft: 36,
                      paddingRight: clauseSearch ? 32 : 14,
                      paddingTop: 9, paddingBottom: 9,
                      borderRadius: 10, border: `1px solid ${BRAND.gray200}`,
                      fontSize: 13, fontFamily: "'Inter', sans-serif",
                      background: BRAND.white, outline: "none",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                      transition: "border-color 0.2s, box-shadow 0.2s",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = BRAND.secondary;
                      e.target.style.boxShadow = `0 0 0 3px ${BRAND.primaryLight}`;
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = BRAND.gray200;
                      e.target.style.boxShadow = "0 1px 2px rgba(0,0,0,0.04)";
                    }}
                  />
                  {clauseSearch && (
                    <button
                      onClick={() => setClauseSearch("")}
                      style={{
                        position: "absolute", right: 10, top: "50%",
                        transform: "translateY(-50%)", background: "none",
                        border: "none", cursor: "pointer", padding: 2,
                        color: BRAND.gray400, display: "flex", alignItems: "center",
                      }}
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>

                {/* Domain Group filter */}
                {domainGroups.length > 0 && (
                  <select
                    value={filterGroup}
                    onChange={(e) => setFilterGroup(e.target.value)}
                    style={{
                      padding: "9px 12px", borderRadius: 10, fontSize: 13,
                      border: `1px solid ${BRAND.gray200}`,
                      fontFamily: "'Inter', sans-serif",
                      background: BRAND.white,
                      color: filterGroup ? BRAND.dark : BRAND.gray400,
                      cursor: "pointer", minWidth: 160, outline: "none",
                    }}
                  >
                    <option value="">All Domain Groups</option>
                    {domainGroups.map((g) => (
                      <option key={g} value={g}>{g}</option>
                    ))}
                  </select>
                )}

  {allClauses.length > 0 && (
  <div style={{ minWidth: 220 }}>
    <Select
      isClearable
      options={allClauses.map(c => ({ label: c, value: c }))}
      value={filterClause ? { label: filterClause, value: filterClause } : null}
      onChange={(sel) => {
      
        setFilterClause(sel?.value || '');
      }}
      placeholder="Filter by clause..."
      classNamePrefix="react-select"
      menuPortalTarget={document.body}
      styles={{
        ...brandSelectStyles,
        menu: (base) => ({ ...base, zIndex: 10001 }),      // ← add this
        menuPortal: (base) => ({ ...base, zIndex: 10001 }), // ← already exists but make sure
      }}
    />
  </div>
)}
                {/* Active chips + Clear all */}
                {(clauseSearch || filterGroup || filterClause) && (
                  <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                    {clauseSearch && (
                      <span style={{
                        padding: "4px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600,
                        background: BRAND.secondaryLight, color: BRAND.secondary,
                        border: `1px solid ${BRAND.secondary}30`,
                        display: "flex", alignItems: "center", gap: 5,
                      }}>
                        "{clauseSearch}"
                        <X size={11} style={{ cursor: "pointer" }} onClick={() => setClauseSearch("")} />
                      </span>
                    )}
                    {filterGroup && (
                      <span style={{
                        padding: "4px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600,
                        background: BRAND.primaryLight, color: BRAND.primaryDark,
                        border: `1px solid ${BRAND.primary}30`,
                        display: "flex", alignItems: "center", gap: 5,
                      }}>
                        {filterGroup}
                        <X size={11} style={{ cursor: "pointer" }} onClick={() => setFilterGroup("")} />
                      </span>
                    )}
                    {filterClause && (
  <span style={{
    padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
    background: '#FEF9C3', color: '#A16207',
    border: '1px solid rgba(245,158,11,0.3)',
    display: 'flex', alignItems: 'center', gap: 5,
  }}>
    {filterClause}
    <X size={11} style={{ cursor: 'pointer' }} onClick={() => setFilterClause('')} />
  </span>
)}
                    <button
                      onClick={() => { setClauseSearch(""); setFilterGroup("");  setFilterClause('');}}
                      style={{
                        fontSize: 11, fontWeight: 600, color: BRAND.gray400,
                        background: "none", border: "none", cursor: "pointer",
                        padding: "2px 4px", textDecoration: "underline",
                        fontFamily: "'Inter', sans-serif",
                      }}
                    >
                      Clear all
                    </button>
                  </div>
                )}

                {/* Result count */}
                <span style={{
                  marginLeft: "auto", fontSize: 12, color: BRAND.gray400,
                  fontFamily: "'Inter', sans-serif", whiteSpace: "nowrap",
                }}>
                  {filteredDomains.length} of {sortedDomains.length} domains
                </span>
              </div>

              {/* All Domains — flat list, 4 per row */}
              {filteredDomains.length === 0 ? (
                <div style={{
                  textAlign: "center", padding: "60px 20px",
                  color: BRAND.gray400, fontFamily: "'Inter', sans-serif",
                  background: BRAND.white, borderRadius: 12,
                  border: `1px dashed ${BRAND.gray200}`,
                }}>
                  <Shield size={36} style={{ marginBottom: 12, opacity: 0.3 }} />
                  <p style={{ fontSize: 14, fontWeight: 600, margin: "0 0 4px", color: BRAND.gray600 }}>
                    No domains match your search
                  </p>
                  <p style={{ fontSize: 12, margin: 0 }}>
                    Try a different keyword or clear the filters
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {filteredDomains.map((domain) => (
                    <Link
                      key={domain.domainId}
                      to={`/trust/domain/${domain.domainId}`}
                      className="block bg-white border rounded-xl hover:shadow-md transition-all group"
                      style={{ borderColor: BRAND.gray200, padding: "20px 20px 16px", position: "relative", overflow: "hidden", textDecoration: "none" }}
                      onMouseEnter={(e) => e.currentTarget.style.borderColor = BRAND.primary}
                      onMouseLeave={(e) => e.currentTarget.style.borderColor = BRAND.gray200}
                    >
                      {/* Top accent bar */}
                      <div style={{
                        position: "absolute",
                        top: 0, left: 0, right: 0,
                        height: 3,
                      }} />

                      <div className="flex items-start justify-between" style={{ marginBottom: 14 }}>
                        <div>
                          <span className="text-xs" style={{
                            color: BRAND.secondary,
                            background: BRAND.secondaryLight,
                            padding: "2px 8px",
                            borderRadius: 5,
                            fontWeight: 550,
                            fontSize: 14,
                          }}>
                            {domain.domainId}
                          </span>
                          <h3 className="font-semibold text-gray-900 leading-snug" style={{
                            marginTop: 10,
                            fontSize: 13.5,
                            minHeight: 38,
                            marginBottom: 0,
                          }}>
                            {domain.domainName}
                          </h3>
                        </div>
                        <ChevronRight className="w-4 h-4 transition-colors flex-shrink-0 mt-1" style={{ color: BRAND.gray400 }} />
                      </div>

                      {/* Divider */}
                      <div style={{ height: 1, background: BRAND.gray100, marginBottom: 12 }} />

                      <div className="flex gap-3 text-xs" style={{ color: BRAND.gray400 }}>
                        <span className="flex items-center gap-1">
                          <Globe className="w-3 h-3" />
                          {domain.primaryCount} primary
                        </span>
                        <span>{domain.secondaryCount} sec</span>
                        <span style={{ color: BRAND.primary, fontWeight: 600 }}>{domain.totalFrameworks} total</span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}

            </div>
          </div>
)}
          <BackgroundFooter isSidebarCollapsed={isSidebarCollapsed} />
        </Col>
      </Col>

      {renderOverviewPopup()}

      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 10002, top: "70px" }}>
        <Toast show={showToast} onClose={() => setShowToast(false)} bg={toastVariant} className="border-0" autohide delay={3000}>
          <Toast.Header>
            <strong className="me-auto">{toastVariant === "success" ? "Success" : "Error"}</strong>
          </Toast.Header>
          <Toast.Body className="text-white">{toastMsg}</Toast.Body>
        </Toast>
      </ToastContainer>
    </Row>
  );
}
